/**
 * Asset Placement Service
 * Uses AWS Bedrock (via backend) to generate 3D coordinates + sizes for VR training assets.
 */

import { ollamaService } from './ollamaService';

const SYSTEM_PROMPT = `You are a VR training scene layout expert. Place 3D training assets with accurate 3D coordinates and real-world sizes.

COORDINATE SYSTEM (Three.js, Y-up):
- The building is centered at origin (0,0,0). The rooftop spans X: -3 to +3, Z: -3 to +3.
- Y = height in meters:
    0.0  = ground floor level
    3.5  = rooftop flat surface — solar panels go HERE
    3.8  = rooftop parapet/back wall — inverter, DCDB, ACDB, meter go HERE

PLACEMENT RULES — STRICT COORDINATE BOUNDS:
- Solar panels (rooftop flat): Y=3.5, X: -2.5 to +1.5, Z: -2.0 to +0.5, rotationX=-15, surface="rooftop"
- Inverter, DCDB, ACDB, meter (parapet wall): Y=3.8, X: -1.5 to +2.5, Z: -2.8 to -2.2, spaced 0.7m apart on X, surface="rooftop_wall"
- ALL rooftop/parapet assets must have X between -3 and +3, Z between -3 and +3
- Space items at least 0.5m apart on X axis

SIZE RULES (use real-world dimensions for a solar installation):
- Solar panel: width_cm=100, height_cm=5, depth_cm=165
- Inverter: width_cm=35, height_cm=50, depth_cm=18
- DCDB / AC Distribution Box / ACDB: width_cm=30, height_cm=40, depth_cm=15
- Electricity meter / Energy meter: width_cm=20, height_cm=25, depth_cm=10
- For any other asset, estimate realistic dimensions based on its name and description.

CRITICAL: Use the EXACT index number from the asset list (1-based).
Return ONLY a valid JSON array, no markdown, no extra text:
[{"index":1,"name":"exact asset name as given","x":0.0,"y":3.5,"z":0.0,"rotationX":0,"rotationY":0,"rotationZ":0,"surface":"rooftop","width_cm":100,"height_cm":5,"depth_cm":165,"confidence":0.9}]`;

function buildAssetList(assets) {
  return assets.map((asset, i) => {
    const surface = asset.placement_context?.surface || 'unknown';
    const dims = asset.realDimensions
      ? `${Math.round(asset.realDimensions.width)}cm W × ${Math.round(asset.realDimensions.height)}cm H`
      : '';
    return `[${i + 1}] "${asset.name}" | Surface: ${surface}${dims ? ` | Size: ${dims}` : ''}${asset.description ? ` | ${asset.description}` : ''}`;
  }).join('\n');
}

function parseJsonArray(text) {
  text = text.replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/, '').trim();
  const start = text.indexOf('[');
  let end = text.lastIndexOf(']');

  if (start !== -1 && end === -1) {
    // Attempt to repair truncated JSON
    let repaired = text;
    if ((repaired.match(/"/g) || []).length % 2 !== 0) repaired += '"';
    if ((repaired.match(/\{/g) || []).length > (repaired.match(/\}/g) || []).length) repaired += '}';
    repaired += ']';
    end = repaired.lastIndexOf(']');
    text = repaired;
  }

  if (start === -1 || end === -1 || end < start) {
    throw new Error('No valid JSON array in response');
  }

  return JSON.parse(text.substring(start, end + 1));
}

/**
 * Generate 3D placement coordinates + sizes for all assets using Bedrock.
 */
export async function generatePlacement(sopText, assets) {
  if (!assets || assets.length === 0) throw new Error('No assets provided');

  const prompt = `${SYSTEM_PROMPT}

SOP:
${sopText.substring(0, 2000)}

ASSETS TO PLACE (${assets.length}):
${buildAssetList(assets)}

Output the JSON placement array for ALL ${assets.length} assets:`;

  const text = await ollamaService.generate(prompt, {
    temperature: 0.1,
    maxTokens: 4096,
  });

  return parseJsonArray(text);
}

/**
 * Match placement result to an asset by index (primary) then name (fallback).
 */
export function matchPlacement(assetName, placements, assetIndex = null) {
  if (!placements || placements.length === 0) return null;

  if (assetIndex !== null) {
    const byIndex = placements.find(p => p.index === assetIndex + 1);
    if (byIndex) return byIndex;
  }

  const name = assetName.toLowerCase().trim();

  let match = placements.find(p => p.name.toLowerCase().trim() === name);
  if (match) return match;

  match = placements.find(p => {
    const pName = p.name.toLowerCase().trim();
    return name.includes(pName) || pName.includes(name);
  });
  if (match) return match;

  const nameWords = name.split(/\s+/).filter(w => w.length > 2);
  return placements.find(p => {
    const pWords = p.name.toLowerCase().split(/\s+/);
    return nameWords.some(w => pWords.includes(w));
  }) || null;
}
